import { useState } from 'react'
import { Button, Select } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import { ChangeText } from '../components/ChangeText'
import { ClosureTimeChart } from '../components/ClosureTimeChart'
import { InfoIcon } from '../components/InfoIcon'
import { SectionCard } from '../components/SectionCard'
import { formatCount, formatDateRange, formatDays } from '../format'
import {
  useFamilyTrends,
  usePopulationFamilies,
  type CohortTarget,
  type FamilyGroup,
  type PopulationQuery,
  type TrendPoint,
  type TrendScope,
  type usePopulationMeasures,
} from '../hooks'
import { familyTheme } from '../theme'
import { ClosureTrend } from './ClosureTrend'

const ALL = 'all'
const ALL_LABEL = 'All families'

function scopeOf(value: string): TrendScope {
  const [type, slug] = value.split(':')
  if (type === 'group') return { groupSlug: slug }
  if (type === 'measure') return { measureSlug: slug }
  return {}
}

/**
 * One figure of a card's grid: `contents`, so its label and value sit in the grid's two rows
 * — labels bottom-aligned, values on one line whatever the labels wrap to.
 */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="contents">
      <dt className="self-end text-xs leading-tight text-slate-500">{label}</dt>
      <dd className="text-xl font-semibold whitespace-nowrap text-slate-900 tabular-nums">
        {value}
      </dd>
    </div>
  )
}

function FamilyCard({
  group,
  selected,
  sparkline,
  sparklineFailed,
  onRetrySparkline,
  onOpen,
}: {
  group: FamilyGroup
  selected: boolean
  sparkline: TrendPoint[] | undefined
  sparklineFailed: boolean
  onRetrySparkline: () => void
  onOpen: () => void
}) {
  const theme = familyTheme(group.slug)
  const Icon = theme.icon
  const change = group.closureTime.changeDays
  // Room for the sparkline while it loads, fails or has a point to draw; none when no slice
  // resolved anything, so the figures center in the card instead of over a blank chart.
  const showSparkline =
    sparklineFailed || sparkline === undefined || sparkline.some((p) => p.medianDays !== null)
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${group.label} family patients`}
      onClick={onOpen}
      onKeyDown={(event) => {
        // Only keys on the card itself — not ones bubbling up from a control inside it.
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen()
        }
      }}
      className={`group flex cursor-pointer flex-col rounded-lg border border-t-[3px] border-slate-200 bg-white p-4 shadow-xs transition hover:shadow-md ${theme.border} ${selected ? 'ring-2 ring-blue-200' : ''}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex size-9 shrink-0 items-center justify-center rounded-md ${theme.tile}`}
        >
          <Icon size={20} stroke={1.75} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="leading-snug font-semibold text-slate-900">{group.label}</div>
          <div className="text-xs text-slate-500">
            {group.focusAreas} of {group.activeMeasures} areas of focus
          </div>
        </div>
        <IconChevronRight
          size={16}
          className="mt-1 shrink-0 text-slate-400 transition group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>
      <div className="flex flex-1 flex-col justify-center gap-2 py-3">
        <dl className="grid grid-flow-col grid-cols-3 grid-rows-[auto_auto] gap-x-2 gap-y-1">
          <Figure label="Unique patients" value={formatCount(group.uniquePatients)} />
          <Figure label="Open care gaps" value={formatCount(group.openCareGaps)} />
          <Figure
            label="Median resolution time"
            value={formatDays(group.closureTime.current.medianDays)}
          />
        </dl>
        {showSparkline && (
          <div className="min-h-11">
            {sparklineFailed ? (
              <p className="text-xs text-slate-500">
                Trend unavailable{' '}
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={(event) => {
                    event.stopPropagation()
                    onRetrySparkline()
                  }}
                >
                  Retry
                </Button>
              </p>
            ) : (
              sparkline && (
                <ClosureTimeChart
                  points={sparkline}
                  subject={group.label}
                  variant="sparkline"
                  worse={change !== null && Math.round(change) > 0}
                />
              )
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <span className="text-[11px] text-slate-500">
          {sparkline ? `${sparkline.length} intervals` : ''}
        </span>
        <ChangeText value={change} unit="days" better="down" />
      </div>
    </div>
  )
}

/**
 * Clinical-family cards (per family group) with sparklines, and below them the time-to-resolve
 * trend and open-gap aging, which the scope selector switches between all families, one
 * family group and one measure. A card opens the drawer on that group's patients.
 */
export function FamilyCards({
  query,
  measures,
  onOpenCohort,
}: {
  query: PopulationQuery
  measures: ReturnType<typeof usePopulationMeasures>
  onOpenCohort: (target: CohortTarget) => void
}) {
  const families = usePopulationFamilies(query)
  const groups = families.data?.groups ?? []
  const measureList = measures.data?.measures ?? []
  const sparklines = useFamilyTrends(
    query,
    groups.map((g) => g.slug),
  )
  const [selected, setSelected] = useState(ALL)
  const scopeData = [
    { group: 'All', items: [{ value: ALL, label: ALL_LABEL }] },
    { group: 'Families', items: groups.map((g) => ({ value: `group:${g.slug}`, label: g.label })) },
    {
      group: 'Measures',
      items: measureList.map((m) => ({ value: `measure:${m.slug}`, label: m.label })),
    },
  ]
  // A filter change (e.g. Type) can drop the selected family or measure from the lists, and a
  // failed request can empty them; the trend then falls back to all, so it never narrows under
  // the all-families label. (The lists keep their previous data while refetching.)
  const selectedItem = scopeData.flatMap((g) => g.items).find((item) => item.value === selected)
  const scopeValue = selectedItem ? selected : ALL
  const scope = scopeOf(scopeValue)
  const subject = selectedItem && scopeValue !== ALL ? selectedItem.label : ALL_LABEL
  const definitions = families.data?.definitions
  const window = families.data?.window

  // The trend row sits beside the cards' section, not inside it: it has its own query, so a
  // failed or loading families request must not hide or dim it.
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        title="Focus by clinical family"
        subtitle={
          window &&
          `Median time to resolve · ${formatDateRange(window.current)} compared with ${formatDateRange(window.comparison)}`
        }
        frame="open"
        query={families}
        isEmpty={groups.length === 0}
        emptyText="No clinical families have an active measure."
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          {groups.map((group, i) => (
            <FamilyCard
              key={group.slug}
              group={group}
              selected={group.slug === scope.groupSlug}
              sparkline={sparklines[i]?.data?.points}
              sparklineFailed={sparklines[i]?.isError ?? false}
              onRetrySparkline={() => void sparklines[i]?.refetch()}
              onOpen={() => onOpenCohort({ type: 'family', slug: group.slug })}
            />
          ))}
        </div>

        {definitions && (
          <p className="mt-3 flex flex-wrap items-center gap-x-1 text-xs text-slate-500">
            Open care gaps can exceed patients, because one patient can have several, and families
            are not additive.
            <span className="inline-flex items-center">
              Unique patients
              <InfoIcon label="Unique patients" definition={definitions.uniquePatients} />
            </span>
            <span className="inline-flex items-center">
              Open care gaps
              <InfoIcon label="Open care gaps" definition={definitions.openCareGaps} />
            </span>
            <span className="inline-flex items-center">
              Median resolution time
              <InfoIcon label="Median resolution time" definition={definitions.closureTime} />
            </span>
            <span className="inline-flex items-center">
              Areas of focus
              <InfoIcon label="Areas of focus" definition={definitions.focusAreas} />
            </span>
          </p>
        )}
      </SectionCard>
      <ClosureTrend
        query={query}
        scope={scope}
        subject={subject}
        measures={measures}
        onOpenCohort={onOpenCohort}
        // With the trend, not the cards: it stays usable when only one of its lists loaded.
        scopeControl={
          (groups.length > 0 || measureList.length > 0) && (
            <Select
              aria-label="Trend scope"
              size="xs"
              w={240}
              searchable
              allowDeselect={false}
              data={scopeData}
              value={scopeValue}
              onChange={(value) => setSelected(value ?? ALL)}
            />
          )
        }
      />
    </div>
  )
}
